import { supabase } from './supabase';

/**
 * Debug utility to check if proposal_events table exists and has the expected structure
 */
export async function checkProposalEventsTable() {
  try {
    console.log('Checking proposal_events table...');
    
    if (!supabase) {
      console.error('Supabase client is not initialized');
      return false;
    }
    
    // Check if proposal_events table exists in pg_tables
    const { data: tableInfo, error: tableError } = await supabase
      .from('pg_tables')
      .select('*')
      .eq('tablename', 'proposal_events')
      .limit(1);
    
    if (tableError) {
      console.error('Error checking proposal_events table:', tableError);
      return false;
    }
    
    if (!tableInfo || tableInfo.length === 0) {
      console.error('proposal_events table does not exist');
      return false;
    }
    
    console.log('proposal_events table exists:', tableInfo);
    
    // Try to directly query the proposal_events table
    const { data: eventData, error: eventError } = await supabase
      .from('proposal_events')
      .select('id, proposal_id, event_type, event_timestamp')
      .limit(5);
    
    if (eventError) {
      console.error('Error querying proposal_events table:', eventError);
      return false;
    }
    
    console.log('proposal_events sample data:', eventData);
    
    // Try to check if the log_proposal_event function exists
    const { data: functionData, error: functionError } = await supabase
      .rpc('pg_function_exists', { function_name: 'log_proposal_event' });
    
    if (functionError) {
      console.error('Error checking log_proposal_event function:', functionError);
      return false;
    }
    
    console.log('log_proposal_event function exists:', functionData);
    
    return true;
  } catch (error) {
    console.error('Exception checking proposal_events table:', error);
    return false;
  }
}

/**
 * Debug utility to directly query proposal events for a specific proposal
 */
export async function directQueryProposalEvents(proposalId: string) {
  try {
    console.log(`Directly querying events for proposal: ${proposalId}`);
    
    if (!supabase) {
      console.error('Supabase client is not initialized');
      return null;
    }
    
    // Directly query the proposal_events table without joins
    const { data, error } = await supabase
      .from('proposal_events')
      .select('*')
      .eq('proposal_id', proposalId)
      .order('event_timestamp', { ascending: false });
    
    if (error) {
      console.error('Error directly querying proposal events:', error);
      return null;
    }
    
    console.log(`Found ${data?.length || 0} events for proposal ${proposalId}:`, data);
    return data;
  } catch (error) {
    console.error('Exception directly querying proposal events:', error);
    return null;
  }
}

/**
 * Debug utility to create a test event for a proposal
 */
export async function createTestProposalEvent(proposalId: string, businessId: string) {
  try {
    console.log(`Creating test event for proposal: ${proposalId}`);
    
    if (!supabase) {
      console.error('Supabase client is not initialized');
      return false;
    }
    
    // Try to directly insert an event
    const { data, error } = await supabase
      .from('proposal_events')
      .insert([{
        proposal_id: proposalId,
        business_id: businessId,
        event_type: 'test_event',
        message: 'This is a test event created via debugUtils'
      }])
      .select();
    
    if (error) {
      console.error('Error creating test proposal event:', error);
      return false;
    }
    
    console.log('Test event created successfully:', data);
    return true;
  } catch (error) {
    console.error('Exception creating test proposal event:', error);
    return false;
  }
}

/**
 * Debug utility to check if the log_proposal_event function works
 */
export async function testLogProposalEvent(proposalId: string, businessId: string) {
  try {
    console.log(`Testing log_proposal_event for proposal: ${proposalId}`);
    
    if (!supabase) {
      console.error('Supabase client is not initialized');
      return false;
    }
    
    // Try to call the log_proposal_event function
    const { data, error } = await supabase
      .rpc('log_proposal_event', {
        p_proposal_id: proposalId,
        p_business_id: businessId,
        p_event_type: 'test_log_event',
        p_message: 'This is a test event via log_proposal_event',
        p_metadata: { source: 'debugUtils' }
      });
    
    if (error) {
      console.error('Error calling log_proposal_event:', error);
      return false;
    }
    
    console.log('log_proposal_event called successfully:', data);
    return true;
  } catch (error) {
    console.error('Exception calling log_proposal_event:', error);
    return false;
  }
}

/**
 * Debug utility to simulate various types of proposal events
 */
export async function simulateProposalEvents(proposalId: string, businessId: string) {
  try {
    console.log(`Simulating various events for proposal: ${proposalId}`);
    
    if (!supabase) {
      console.error('Supabase client is not initialized');
      return false;
    }
    
    // Array of event types to simulate
    const eventTypes = [
      { type: 'created', message: 'Proposal was created', metadata: { source: 'simulation' } },
      { type: 'sent_email', message: 'Proposal was sent via email', metadata: { method: 'email', recipient: 'test@example.com' } },
      { type: 'sent_sms', message: 'Proposal was sent via SMS', metadata: { method: 'sms', recipient: '+1234567890' } },
      { type: 'viewed', message: 'Proposal was viewed by customer', metadata: { source: 'simulation', viewed_at: new Date().toISOString() } },
      { type: 'offer_approved', message: 'Offer was approved', metadata: { offer_id: '00000000-0000-0000-0000-000000000000' } },
      { type: 'payment_received', message: 'Payment was received', metadata: { amount: 100, currency: 'USD' } },
      { type: 'booked', message: 'Converted to booking', metadata: { booking_id: '00000000-0000-0000-0000-000000000000' } }
    ];
    
    // Create all events with 5 second spacing
    for (let i = 0; i < eventTypes.length; i++) {
      const event = eventTypes[i];
      const { data, error } = await supabase
        .from('proposal_events')
        .insert([{
          proposal_id: proposalId,
          business_id: businessId,
          event_type: event.type,
          message: event.message,
          metadata: event.metadata,
          // Set timestamp to 5 seconds apart for each event, starting from current time - (total events * 5 seconds)
          event_timestamp: new Date(Date.now() - ((eventTypes.length - i) * 5000)).toISOString()
        }])
        .select();
      
      if (error) {
        console.error(`Error creating ${event.type} event:`, error);
        continue;
      }
      
      console.log(`Created ${event.type} event:`, data);
    }
    
    return true;
  } catch (error) {
    console.error('Exception simulating proposal events:', error);
    return false;
  }
}

// Helper function to check if a PostgreSQL function exists
// Can be used in RPC
export async function pgFunctionExists(functionName: string) {
  try {
    if (!supabase) {
      console.error('Supabase client is not initialized');
      return false;
    }
    
    const { data, error } = await supabase
      .rpc('execute_sql', {
        query: `
          SELECT EXISTS (
            SELECT 1 
            FROM pg_proc p 
            JOIN pg_namespace n ON p.pronamespace = n.oid 
            WHERE p.proname = '${functionName}'
            AND n.nspname NOT IN ('pg_catalog', 'information_schema')
          )
        `
      });
    
    if (error) {
      console.error(`Error checking if function ${functionName} exists:`, error);
      return false;
    }
    
    return data && data.length > 0 && data[0].exists;
  } catch (error) {
    console.error(`Exception checking if function ${functionName} exists:`, error);
    return false;
  }
}

/**
 * Creates a test proposal event without requiring a business ID
 * This is a simplified version for testing only in the modal
 */
export async function createTestProposalEventSimple(proposalId: string) {
  if (!supabase) {
    console.error('Supabase client is not initialized');
    return null;
  }

  try {
    // First get the business ID for this proposal
    const { data, error } = await supabase
      .from('proposals')
      .select('business_id')
      .eq('id', proposalId)
      .single();
    
    if (error) {
      console.error('Error fetching proposal:', error);
      throw error;
    }

    const businessId = data.business_id;
    
    // Now we can create a test event with the retrieved business ID
    const eventTypes = ['viewed', 'sent_email', 'sent_sms', 'offer_approved', 'offer_declined', 'payment_received'];
    const randomType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
    
    const { data: eventData, error: eventError } = await supabase.rpc('log_proposal_event', {
      p_proposal_id: proposalId,
      p_business_id: businessId,
      p_event_type: randomType,
      p_message: `Test event - ${randomType} at ${new Date().toISOString()}`,
      p_metadata: { test: true, random_value: Math.random() }
    });

    if (eventError) {
      console.error('Error creating test event:', eventError);
      throw eventError;
    }

    console.log('Created test event:', eventData);
    return eventData;
  } catch (error) {
    console.error('Error in createTestProposalEventSimple:', error);
    throw error;
  }
}

/**
 * Simulates multiple proposal events without requiring a business ID
 * This is a simplified version for testing only in the modal
 */
export async function simulateProposalEventsSimple(proposalId: string) {
  if (!supabase) {
    console.error('Supabase client is not initialized');
    return null;
  }

  try {
    // First get the business ID for this proposal
    const { data, error } = await supabase
      .from('proposals')
      .select('business_id')
      .eq('id', proposalId)
      .single();
    
    if (error) {
      console.error('Error fetching proposal:', error);
      throw error;
    }

    const businessId = data.business_id;
    
    // Create a sequence of common events with appropriate timestamps
    const now = new Date();
    const events = [
      {
        type: 'created',
        message: 'Proposal created',
        timestamp: new Date(now.getTime() - 1000 * 60 * 60 * 24 * 2) // 2 days ago
      },
      {
        type: 'sent_email',
        message: 'Proposal sent via email',
        timestamp: new Date(now.getTime() - 1000 * 60 * 60 * 24 * 1.5) // 1.5 days ago
      },
      {
        type: 'viewed',
        message: 'Customer viewed the proposal',
        timestamp: new Date(now.getTime() - 1000 * 60 * 60 * 24) // 1 day ago
      },
      {
        type: 'viewed',
        message: 'Customer viewed the proposal again',
        timestamp: new Date(now.getTime() - 1000 * 60 * 60 * 12) // 12 hours ago
      },
      {
        type: 'offer_approved',
        message: 'Customer approved an offer',
        timestamp: new Date(now.getTime() - 1000 * 60 * 60 * 4) // 4 hours ago
      },
      {
        type: 'payment_received',
        message: 'Payment received for proposal',
        timestamp: new Date(now.getTime() - 1000 * 60 * 30) // 30 minutes ago
      }
    ];
    
    // Insert events with timestamps
    for (const event of events) {
      const { error: eventError } = await supabase.rpc('log_proposal_event_with_timestamp', {
        p_proposal_id: proposalId,
        p_business_id: businessId,
        p_event_type: event.type,
        p_message: event.message,
        p_metadata: { test: true, simulated: true },
        p_timestamp: event.timestamp.toISOString()
      });

      if (eventError) {
        console.error(`Error creating ${event.type} event:`, eventError);
        // Continue with other events instead of failing completely
      }
    }

    return events;
  } catch (error) {
    console.error('Error in simulateProposalEventsSimple:', error);
    throw error;
  }
} 